import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { apiService } from "../services/api";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import UsageDisplay from "../components/common/UsageDisplay";
import { Salesperson } from "../types/api";
import SalespersonUsageDisplay from "../components/common/SalespersonUsageDisplay";
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  UserPlusIcon,
  FolderOpenIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  MicrophoneIcon,
} from "@heroicons/react/24/outline";
import AddSalespersonModal from "../components/forms/AddSalespersonModal";
import toast from "react-hot-toast";

interface FileWithStatus {
  file: File;
  id: string;
  status:
    | "pending"
    | "uploading"
    | "transcribing"
    | "analyzing"
    | "completed"
    | "error";
  progress: number;
  callId?: string;
  error?: string;
  salespersonId?: string;
}

interface UploadStatus {
  status: "idle" | "uploading" | "processing" | "completed" | "error";
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  currentFile?: string;
}

const UploadCall: React.FC = () => {
  const [selectedFiles, setSelectedFiles] = useState<FileWithStatus[]>([]);
  const [salespeople, setSalespeople] = useState<Salesperson[]>([]);
  const [selectedSalesperson, setSelectedSalesperson] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>({
    status: "idle",
    totalFiles: 0,
    completedFiles: 0,
    failedFiles: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [assignSalespersonToAll, setAssignSalespersonToAll] = useState(true);
  const { user, isSalesperson, isManager } = useAuth();
  const [usageStats, setUsageStats] = useState<any>(null);
  // const [estimatedDuration, setEstimatedDuration] = useState<number>(0);

  const [salespersonUsage, setSalespersonUsage] = useState<any>(null);

  const navigate = useNavigate();
  const { onCallStatus, onBatchStatus, onCallError, isConnected } = useSocket();

  // Set up Socket.IO listeners
  useEffect(() => {
    const unsubscribeCallStatus = onCallStatus((data) => {
      setSelectedFiles((files) =>
        files.map((f) => {
          if (f.callId === data.callId) {
            return {
              ...f,
              status: data.status as any,
              progress: data.progress,
            };
          }
          return f;
        })
      );

      // Update completion counters
      if (data.status === "completed") {
        setUploadStatus((prev) => ({
          ...prev,
          completedFiles: prev.completedFiles + 1,
        }));

        toast.success(`Analysis complete for ${data.filename || "file"}!`);
      }
    });

    const unsubscribeBatchStatus = onBatchStatus((data) => {
      setUploadStatus((prev) => ({
        ...prev,
        status: data.status === "completed" ? "completed" : "processing",
        completedFiles: data.completedFiles,
        failedFiles: data.failedFiles,
      }));

      // Update individual file statuses
      data.files.forEach((fileUpdate) => {
        setSelectedFiles((files) =>
          files.map((f) => {
            if (
              f.callId === fileUpdate.callId ||
              f.file.name === fileUpdate.filename
            ) {
              return {
                ...f,
                status: fileUpdate.status as any,
                progress: fileUpdate.progress,
                error: fileUpdate.error,
              };
            }
            return f;
          })
        );
      });

      // Show completion message
      if (data.status === "completed") {
        if (data.failedFiles === 0) {
          toast.success(`All ${data.completedFiles} files completed!`);
        } else {
          toast.success(
            `${data.completedFiles} completed, ${data.failedFiles} failed`
          );
        }
      }
    });

    const unsubscribeCallError = onCallError((data) => {
      setSelectedFiles((files) =>
        files.map((f) => {
          if (f.callId === data.callId) {
            return {
              ...f,
              status: "error",
              error: data.error,
            };
          }
          return f;
        })
      );

      setUploadStatus((prev) => ({
        ...prev,
        failedFiles: prev.failedFiles + 1,
      }));

      toast.error(`Error processing ${data.filename || "file"}: ${data.error}`);
    });

    return () => {
      unsubscribeCallStatus();
      unsubscribeBatchStatus();
      unsubscribeCallError();
    };
  }, [onCallStatus, onBatchStatus, onCallError, isConnected]);

  const loadSalespeople = useCallback(async () => {
    if (!isManager) return;

    try {
      const response = await apiService.getSalespeopleList();
      if (response.success) {
        setSalespeople(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load salespeople:", error);
      toast.error("Failed to load salespeople");
    } finally {
      setLoading(false);
    }
  }, [isManager]); // Add dependency

  const loadUsageStats = useCallback(async () => {
    try {
      if (isSalesperson && user?.salespersonId) {
        const salespersonResponse = await apiService.getSalesperson(
          user.salespersonId
        );
        if (salespersonResponse.success && salespersonResponse.data) {
          setSalespersonUsage({
            secondsCurrentPeriod:
              salespersonResponse.data.secondsCurrentPeriod || 0,
            monthlyMinuteLimit: salespersonResponse.data.monthlyMinuteLimit,
            periodEnd: null,
          });

          const companyResponse = await apiService.getCompanyUsageStats();
          if (companyResponse.success) {
            const periodEnd = companyResponse.data?.current_period_end;
            if (periodEnd) {
              setSalespersonUsage((prev: any) => ({
                ...prev,
                periodEnd,
              }));
            }
          }
        }
      } else {
        const response = await apiService.getCompanyUsageStats();
        if (response.success) {
          setUsageStats(response.data);
        }
      }
    } catch (error) {
      console.error("Failed to load usage stats:", error);
    }
  }, [isSalesperson, user?.salespersonId]); // Add dependencies

  useEffect(() => {
    if (isManager) {
      loadSalespeople();
      loadUsageStats();
    } else if (isSalesperson && user?.salespersonId) {
      setSelectedSalesperson(user.salespersonId);
      setAssignSalespersonToAll(true);
      loadUsageStats();
      setLoading(false);
    }
  }, [isManager, isSalesperson, user, loadSalespeople, loadUsageStats]); // Add all dependencies

  const generateFileId = () => {
    return Math.random().toString(36).substr(2, 9);
  };

  const validateAudioFile = (
    file: File
  ): { isValid: boolean; error?: string } => {
    const supportedExtensions = [
      ".mp3",
      ".wav",
      ".m4a",
      ".aac",
      ".ogg",
      ".flac",
    ];
    const maxSize = 50 * 1024 * 1024; // 50MB

    const isAudio =
      file.type.startsWith("audio/") ||
      supportedExtensions.some((ext) => file.name.toLowerCase().endsWith(ext));

    if (!isAudio) {
      return { isValid: false, error: "Not a supported audio file format" };
    }

    if (file.size > maxSize) {
      return { isValid: false, error: "File exceeds 50MB size limit" };
    }

    if (file.size < 1024) {
      return { isValid: false, error: "File appears to be empty or corrupted" };
    }

    return { isValid: true };
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const newFiles: FileWithStatus[] = [];
      const errors: string[] = [];

      // Get the salesperson ID - for salespeople it's always their own ID
      const salespersonId = isSalesperson
        ? user?.salespersonId
        : selectedSalesperson;

      acceptedFiles.forEach((file) => {
        const validation = validateAudioFile(file);

        if (validation.isValid) {
          newFiles.push({
            file,
            id: generateFileId(),
            status: "pending",
            progress: 0,
            salespersonId: salespersonId, // Just use the salespersonId directly
          });
        } else {
          errors.push(`${file.name}: ${validation.error}`);
        }
      });

      if (errors.length > 0) {
        toast.error(
          `${errors.length} file(s) rejected:\n${errors.join("\n")}`,
          {
            duration: 6000,
          }
        );
      }

      if (newFiles.length > 0) {
        setSelectedFiles((prev) => [...prev, ...newFiles]);
        toast.success(`${newFiles.length} file(s) added successfully`);
      }
    },
    [isSalesperson, user, selectedSalesperson]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "audio/*": [".mp3", ".wav", ".m4a", ".aac", ".ogg", ".flac"],
    },
    multiple: true,
  });

  const removeFile = (fileId: string) => {
    setSelectedFiles((files) => files.filter((f) => f.id !== fileId));
  };

  const updateFileStatus = (
    fileId: string,
    updates: Partial<FileWithStatus>
  ) => {
    setSelectedFiles((files) =>
      files.map((f) => (f.id === fileId ? { ...f, ...updates } : f))
    );
  };

  const assignSalespersonToFile = (fileId: string, salespersonId: string) => {
    updateFileStatus(fileId, { salespersonId });
  };

  const filteredSalespeople = salespeople.filter(
    (person) =>
      person.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${person.firstName} ${person.lastName}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase())
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const handleBatchUpload = async () => {
    // Validation
    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    const filesWithoutSalesperson = selectedFiles.filter(
      (f) => !f.salespersonId
    );
    if (filesWithoutSalesperson.length > 0) {
      if (isSalesperson) {
        toast.error(
          "Error: Unable to assign files to your account. Please try logging out and back in."
        );
      } else {
        toast.error(
          `${filesWithoutSalesperson.length} file(s) don't have a salesperson assigned. Please select a salesperson first.`
        );
      }
      return;
    }

    if (isManager) {
      const invalidAssignments = selectedFiles.filter((f) => {
        return (
          f.salespersonId &&
          !salespeople.find((sp) => sp.id === f.salespersonId)
        );
      });

      if (invalidAssignments.length > 0) {
        toast.error("Some files have invalid salesperson assignments");
        return;
      }
    }

    // Check socket connection
    if (!isConnected) {
      toast.error("Not connected to server. Please refresh and try again.");
      return;
    }

    setUploadStatus({
      status: "uploading",
      totalFiles: selectedFiles.length,
      completedFiles: 0,
      failedFiles: 0,
    });

    try {
      // Prepare form data
      const formData = new FormData();
      const salespersonAssignments: { [key: string]: string } = {};

      selectedFiles.forEach((fileWithStatus) => {
        formData.append("audioFiles", fileWithStatus.file);
        salespersonAssignments[fileWithStatus.file.name] =
          fileWithStatus.salespersonId!;
      });

      formData.append(
        "salespersonAssignments",
        JSON.stringify(salespersonAssignments)
      );

      const batchSalespersonId = isSalesperson
        ? user?.salespersonId
        : selectedFiles[0]?.salespersonId;

      if (batchSalespersonId) {
        formData.append("salespersonId", batchSalespersonId);
      }

      // Upload batch
      const response = await apiService.uploadCallsBatch(
        formData,
        (progress) => {}
      );

      if (response.success && response.data) {
        // Update files with call IDs
        response.data.results.forEach((result: any) => {
          if (result.status === "success" && result.callId) {
            setSelectedFiles((files) =>
              files.map((f) => {
                if (f.file.name === result.filename) {
                  return {
                    ...f,
                    callId: result.callId,
                    status: "uploading",
                    progress: 0,
                  };
                }
                return f;
              })
            );
          }
        });

        toast.success("Upload started! Processing files...");
      } else {
        toast.error(response.message || "Upload failed");
        setUploadStatus((prev) => ({ ...prev, status: "error" }));
      }
    } catch (error: any) {
      console.error("Upload error:", error);

      // Check if it's a usage limit error
      if (
        error.response?.status === 403 &&
        error.response?.data?.data?.overageAmount
      ) {
        const data = error.response.data.data;
        const limitType = data.limitType || "company";

        if (limitType === "salesperson") {
          toast.error(
            `Upload exceeds your monthly limit by ${Math.ceil(
              data.overageAmount / 60
            )} minutes. ` +
              `You have ${Math.ceil(
                data.remainingSeconds / 60
              )} minutes remaining. Contact your manager for more capacity.`,
            { duration: 8000 }
          );
        } else {
          toast.error(
            `Upload exceeds monthly limit by ${Math.ceil(
              data.overageAmount / 60
            )} minutes. ` +
              `You have ${Math.ceil(
                data.remainingSeconds / 60
              )} minutes remaining (${Math.ceil(
                data.graceSeconds / 60
              )} minute grace period).`,
            { duration: 8000 }
          );
        }
      } else {
        toast.error(error.response?.data?.message || "Failed to upload call");
      }

      setLoading(false);
      setUploadStatus({
        status: "idle",
        totalFiles: 0,
        completedFiles: 0,
        failedFiles: 0,
      });
    }
  };

  const resetUpload = () => {
    setUploadStatus({
      status: "idle",
      totalFiles: 0,
      completedFiles: 0,
      failedFiles: 0,
    });
    setSelectedFiles([]);
    setSelectedSalesperson("");
    setSearchTerm("");
  };

  const getStatusIcon = (status: FileWithStatus["status"]) => {
    switch (status) {
      case "pending":
        return <ClockIcon className="h-5 w-5 text-gray-400" />;
      case "uploading":
      case "transcribing":
      case "analyzing":
        return (
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
        );
      case "completed":
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case "error":
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <DocumentIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: FileWithStatus["status"]) => {
    switch (status) {
      case "completed":
        return "text-green-600 bg-green-50";
      case "error":
        return "text-red-600 bg-red-50";
      case "uploading":
      case "transcribing":
      case "analyzing":
        return "text-blue-600 bg-blue-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const getStatusText = (status: FileWithStatus["status"]) => {
    switch (status) {
      case "transcribing":
        return "Transcribing";
      case "analyzing":
        return "Analyzing";
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      {/* Usage Display - Manager View */}
      {isManager && usageStats && (
        <div className="mb-6">
          <UsageDisplay
            secondsUsed={usageStats.seconds_used_current_period}
            secondsLimit={usageStats.monthly_minute_limit}
            periodEnd={usageStats.current_period_end}
            compact={false}
          />
        </div>
      )}

      {/* Usage Display - Salesperson View */}
      {isSalesperson && salespersonUsage && salespersonUsage.periodEnd && (
        <div className="mb-6">
          <SalespersonUsageDisplay
            secondsUsed={salespersonUsage.secondsCurrentPeriod}
            secondsLimit={salespersonUsage.monthlyMinuteLimit}
            periodEnd={salespersonUsage.periodEnd}
            compact={false}
          />
        </div>
      )}

      <div className="md:flex md:items-center md:justify-between mb-6">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Upload Sales Calls
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Upload multiple audio files and assign them to salespeople for
            analysis
          </p>
        </div>
        {!isSalesperson && (
          <div className="mt-4 flex md:mt-0 md:ml-4">
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              Add Employee
            </button>
          </div>
        )}
      </div>

      {(uploadStatus.status === "idle" || uploadStatus.status === "error") && (
        <div className="space-y-6">
          {/* Salesperson Assignment Section */}
          {!isSalesperson && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                  Assign Salesperson
                </h3>

                {/* Assignment Mode Toggle */}
                <div className="mb-4">
                  <div className="flex items-center space-x-6">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="assignmentMode"
                        checked={assignSalespersonToAll}
                        onChange={() => setAssignSalespersonToAll(true)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Assign same salesperson to all files
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="assignmentMode"
                        checked={!assignSalespersonToAll}
                        onChange={() => setAssignSalespersonToAll(false)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Assign individually per file
                      </span>
                    </label>
                  </div>
                </div>

                {/* Salesperson Selection */}
                {assignSalespersonToAll && (
                  <div>
                    <div className="relative mb-4">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="text"
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        placeholder="Search salespeople..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md">
                      {filteredSalespeople.length > 0 ? (
                        filteredSalespeople.map((person) => (
                          <label
                            key={person.id}
                            className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <input
                              type="radio"
                              name="salesperson"
                              value={person.id}
                              checked={selectedSalesperson === person.id}
                              onChange={(e) => {
                                setSelectedSalesperson(e.target.value);
                                setSelectedFiles((files) =>
                                  files.map((f) => ({
                                    ...f,
                                    salespersonId: e.target.value,
                                  }))
                                );
                              }}
                              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                            />
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900">
                                {`${person.firstName} ${person.lastName}`}
                              </p>
                              {(person.callCount ?? 0) > 0 && (
                                <p className="text-sm text-gray-500">
                                  {person.callCount} previous calls
                                  {person.avgGrade &&
                                    ` • Avg: ${person.avgGrade}`}
                                </p>
                              )}
                            </div>
                          </label>
                        ))
                      ) : (
                        <div className="p-3 text-center text-gray-500">
                          {searchTerm
                            ? "No salespeople match your search"
                            : "No salespeople found"}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {!assignSalespersonToAll && (
                  <div className="text-sm text-gray-600 italic">
                    You can assign salespeople individually to each file after
                    uploading.
                  </div>
                )}
              </div>
            </div>
          )}

          {isSalesperson && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <div className="flex-shrink-0">
                  <MicrophoneIcon className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Auto-Assignment
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Your uploaded calls will be automatically assigned to you.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* File Upload Area */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div
                {...getRootProps()}
                className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md cursor-pointer hover:border-gray-400 transition-colors ${
                  isDragActive ? "border-indigo-400 bg-indigo-50" : ""
                }`}
              >
                <div className="space-y-1 text-center">
                  <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <input {...getInputProps()} />
                    <p>
                      {isDragActive ? (
                        "Drop the files here..."
                      ) : (
                        <>
                          <span className="font-medium text-indigo-600 hover:text-indigo-500">
                            Upload files or folders
                          </span>{" "}
                          or drag and drop
                        </>
                      )}
                    </p>
                  </div>
                  <p className="text-xs text-gray-500">
                    MP3, WAV, M4A, AAC, OGG, FLAC up to 50MB each
                  </p>
                </div>
              </div>

              {/* Alternative file/folder input */}
              <div className="mt-4 text-center space-x-3">
                <label className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
                  <DocumentIcon className="h-4 w-4 mr-2" />
                  Select File(s)
                  <input
                    type="file"
                    className="sr-only"
                    multiple
                    accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      onDrop(files);
                    }}
                  />
                </label>
                <label className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer">
                  <FolderOpenIcon className="h-4 w-4 mr-2" />
                  Select Folder
                  <input
                    type="file"
                    className="sr-only"
                    multiple
                    // @ts-ignore
                    webkitdirectory=""
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      onDrop(files);
                    }}
                  />
                </label>
              </div>

              {/* Selected Files */}
              {selectedFiles.length > 0 && (
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-900">
                      Selected Files ({selectedFiles.length})
                    </h4>
                  </div>

                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                    {selectedFiles.map((fileWithStatus) => (
                      <div
                        key={fileWithStatus.id}
                        className="p-4 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1 min-w-0">
                            {getStatusIcon(fileWithStatus.status)}
                            <div className="ml-3 flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {fileWithStatus.file.name}
                              </p>
                              <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <span>
                                  {formatFileSize(fileWithStatus.file.size)}
                                </span>
                                <span
                                  className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                                    fileWithStatus.status
                                  )}`}
                                >
                                  {getStatusText(fileWithStatus.status)}
                                </span>
                                {fileWithStatus.progress > 0 &&
                                  fileWithStatus.status !== "completed" && (
                                    <span>
                                      {Math.round(fileWithStatus.progress)}%
                                    </span>
                                  )}
                              </div>
                              {fileWithStatus.error && (
                                <p className="text-sm text-red-600 mt-1">
                                  Error: {fileWithStatus.error}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Salesperson Assignment */}
                          <div className="ml-4 flex items-center space-x-2">
                            {!assignSalespersonToAll ? (
                              <select
                                value={fileWithStatus.salespersonId || ""}
                                onChange={(e) =>
                                  assignSalespersonToFile(
                                    fileWithStatus.id,
                                    e.target.value
                                  )
                                }
                                className="text-xs border border-gray-300 rounded-md px-2 py-1 focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={fileWithStatus.status !== "pending"}
                              >
                                <option value="">Select salesperson</option>
                                {salespeople.map((person) => (
                                  <option key={person.id} value={person.id}>
                                    {person.firstName} {person.lastName}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <span className="text-xs text-gray-500">
                                {fileWithStatus.salespersonId
                                  ? salespeople.find(
                                      (sp) =>
                                        sp.id === fileWithStatus.salespersonId
                                    )
                                    ? `${
                                        salespeople.find(
                                          (sp) =>
                                            sp.id ===
                                            fileWithStatus.salespersonId
                                        )?.firstName
                                      } ${
                                        salespeople.find(
                                          (sp) =>
                                            sp.id ===
                                            fileWithStatus.salespersonId
                                        )?.lastName
                                      }`
                                    : "Unknown"
                                  : "Not assigned"}
                              </span>
                            )}

                            {fileWithStatus.status === "pending" && (
                              <button
                                onClick={() => removeFile(fileWithStatus.id)}
                                className="text-red-400 hover:text-red-500"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Progress Bar */}
                        {fileWithStatus.progress > 0 &&
                          fileWithStatus.status !== "completed" && (
                            <div className="mt-2">
                              <div className="w-full bg-gray-200 rounded-full h-1">
                                <div
                                  className="bg-indigo-600 h-1 rounded-full transition-all duration-300"
                                  style={{
                                    width: `${fileWithStatus.progress}%`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Upload Button */}
          <div className="flex justify-end space-x-4">
            {selectedFiles.length > 0 && (
              <button
                onClick={resetUpload}
                className="px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Clear All
              </button>
            )}
            <button
              onClick={handleBatchUpload}
              disabled={
                selectedFiles.length === 0 ||
                selectedFiles.some((f) => !f.salespersonId) ||
                !isConnected
              }
              className="px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Upload & Analyze{" "}
              {selectedFiles.length > 0
                ? `(${selectedFiles.length} files)`
                : ""}
            </button>
          </div>
        </div>
      )}

      {/* Progress Display */}
      {(uploadStatus.status === "uploading" ||
        uploadStatus.status === "processing") && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              <div className="mb-4">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto"></div>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Processing Files...
              </h3>
              {uploadStatus.currentFile && (
                <p className="text-sm text-gray-600 mb-2">
                  Current: {uploadStatus.currentFile}
                </p>
              )}
              <div className="mb-4">
                <div className="text-sm text-gray-600 mb-2">
                  {uploadStatus.completedFiles} of {uploadStatus.totalFiles}{" "}
                  completed
                  {uploadStatus.failedFiles > 0 &&
                    ` • ${uploadStatus.failedFiles} failed`}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        uploadStatus.totalFiles > 0
                          ? ((uploadStatus.completedFiles +
                              uploadStatus.failedFiles) /
                              uploadStatus.totalFiles) *
                            100
                          : 0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Display */}
      {uploadStatus.status === "completed" && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Upload Complete!
              </h3>
              <div className="text-sm text-gray-600 mb-4">
                <p>
                  {uploadStatus.completedFiles} files successfully processed
                </p>
                {uploadStatus.failedFiles > 0 && (
                  <p className="text-red-600">
                    {uploadStatus.failedFiles} files failed
                  </p>
                )}
              </div>
              <div className="space-x-4">
                <button
                  onClick={resetUpload}
                  className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  Upload More Files
                </button>
                <button
                  onClick={() => navigate("/team")}
                  className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  View All Calls
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Employee Modal */}
      <AddSalespersonModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          setShowAddModal(false);
          loadSalespeople();
        }}
      />
    </div>
  );
};

export default UploadCall;
